import React, { useState } from 'react';
import "./Signup.css"; 
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useNavigate } from 'react-router-dom';
import AuthService from '../../services/auth.service.js';

// 1) Obtenemos el entorno y definimos si está deshabilitado:
const currentEnv = process.env.REACT_APP_ENV;
const isSignUpDisabled = currentEnv === "prod"; // Solo deshabilitar en producción

export default function Signup() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    lastName: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    country: '',
    birthDate: '',
    userName: '',
  });
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [errorMessage, setErrorMessage] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiKey, setApiKey] = useState(null);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);

  // Estados para mostrar/ocultar password
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  // Lógica de manejo de cambios
  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrorMessage('');
    if (name === 'birthDate') {
      let inputVal = value.replace(/\D/g, '');
      let day = inputVal.substring(0, 2);
      let month = inputVal.substring(2, 4);
      let year = inputVal.substring(4, 8);
      let formatted = day;
      if (month) formatted += "/" + month;
      if (year) formatted += "/" + year;
      setFormData({ ...formData, [name]: formatted });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handlePhoneChange = (value, data) => {
    setFormData({
      ...formData,
      phoneNumber: value,
      country: data.name,
    });
  };

  const handleVerificationCodeChange = (index, value) => {
    if (!/^\d$/.test(value) && value !== "") return;
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    if (value && index < verificationCode.length - 1) {
      const nextInput = document.getElementById(`code-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      const newCode = [...verificationCode];
      if (newCode[index] === "" && index > 0) {
        const prevInput = document.getElementById(`code-input-${index - 1}`);
        if (prevInput) prevInput.focus();
        newCode[index - 1] = "";
      } else {
        newCode[index] = "";
      }
      setVerificationCode(newCode);
    }
  };

  const handleVerification = async () => {
    const confirmationCode = verificationCode.join('');
    const email = localStorage.getItem('signup_email') || formData.email;
    // Para Cognito, el username ES el email, no el username personalizado
    const username = email; // ← USAR EMAIL como username para Cognito
    
    setVerificationLoading(true);
    setErrorMessage('');
    
    try {
      await AuthService.verifyEmail(email, username, confirmationCode);
      
      // Recuperar API key si existe
      const tempApiKey = localStorage.getItem('temp_api_key');
      if (tempApiKey) {
        setApiKey(tempApiKey);
        setShowApiKeyModal(true);
        localStorage.removeItem('temp_api_key');
        // NO redirigir inmediatamente, esperar a que el usuario cierre el modal
      } else {
        // Si no hay API key, redirigir directamente
        localStorage.removeItem('signup_email');
        navigate('/login', { 
          state: { 
            message: 'Email verified successfully! Please log in.',
            email: email 
          }
        });
      }
      
      setErrorMessage('');
      
    } catch (error) {
      setErrorMessage('Email verification failed: ' + error.message);
      console.error('Error verifying email', error);
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleSubmit = async () => {
    const { email, password, confirmPassword, name, lastName, phoneNumber, country, birthDate, userName } = formData;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const nameRegex = /^[a-zA-ZÀ-ÿ\s]+$/; 
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    
    // Validaciones requeridas (username personalizado obligatorio)
    if (!email || !password || !confirmPassword || !name || !lastName || !userName) {
      setErrorMessage('Please fill in all required fields (Name, Last Name, Username, Email, Password)');
      return;
    }
    if (!emailRegex.test(email)) {
      setErrorMessage('Please provide a valid email address');
      return;
    }
    if (!nameRegex.test(name) || !nameRegex.test(lastName)) {
      setErrorMessage('Name and Last Name can only contain letters, spaces, and accents');
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }
    if (!passwordRegex.test(password)) {
      setErrorMessage('Password must be at least 8 characters and include uppercase, lowercase, digit, and special character');
      return;
    }
    if (userName.length < 3) {
      setErrorMessage('Username must be at least 3 characters long');
      return;
    }
    
    setLoading(true);
    setErrorMessage('');
    
    try {
      // Backend requiere username personalizado (no email)
      const registrationData = {
        email,
        password,
        username: userName,     // ← Usar el username que ingresa el usuario
        name,               
        last_name: lastName
      };

      const response = await AuthService.register(registrationData);
      
      // Guardar API key para mostrarlo después de la verificación
      if (response?.api_key) {
        setApiKey(response.api_key);
        localStorage.setItem('temp_api_key', response.api_key);
      }
      
      setIsSubmitted(true);
      setErrorMessage('');
      
      // Solo guardar el email para usarlo en la verificación
      localStorage.setItem('signup_email', email);
      
    } catch (error) {
      setErrorMessage(error.message || 'Error signing up. Please try again.');
      console.error('Error signing up', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTermsChange = (e) => {
    setTermsAccepted(e.target.checked);
  };

  const handleDownloadJSON = () => {
    const data = {
      api_key: apiKey,
      email: formData.email,
      username: formData.userName,
      created_at: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `zenit-api-key-${formData.userName}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadTXT = () => {
    const text = `Zenit AI Lab API Key\n\nEmail: ${formData.email}\nUsername: ${formData.userName}\nAPI Key: ${apiKey}\nCreated: ${new Date().toISOString()}\n\nIMPORTANT: Keep this key secure. You won't be able to see it again.`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `zenit-api-key-${formData.userName}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyApiKey = () => {
    navigator.clipboard.writeText(apiKey);
  };

  return (
    <div className="oval-signup-body">
      <div className="oval-signup-container-wrapper">
        <div className="oval-signup-double-background"></div>
        <div className="oval-signup-container">
          {isSignUpDisabled ? (
            <div className="oval-signup-disabled-message">
              <h2>Registration Disabled</h2>
              <p>Currently, registration is disabled</p>
              <a href="/">Return to Home</a>
            </div>
          ) : !isSubmitted ? (
            <div>
              <h2 className="oval-title-outside">Create new account</h2>
              {errorMessage && <div className="oval-error-message">{errorMessage}</div>}
              <div className="oval-form-box">
                <div className="oval-form-grid">
                  {/* Row 1: Name - Last Name */}
                  <div className="oval-field">
                    <label htmlFor="name">Name</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} />
                  </div>
                  <div className="oval-field">
                    <label htmlFor="lastName">Last Name</label>
                    <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} />
                  </div>
                  {/* Row 2: Date of Birth - Username */}
                  <div className="oval-field">
                    <label htmlFor="birthDate">Date of Birth</label>
                    <input type="text" id="birthDate" name="birthDate" placeholder="dd/mm/yyyy" value={formData.birthDate} onChange={handleChange} />
                  </div>
                  <div className="oval-field">
                    <label htmlFor="userName">Username</label>
                    <input type="text" id="userName" name="userName" value={formData.userName} onChange={handleChange} />
                  </div>
                  {/* Row 3: Email - Phone Number */}
                  <div className="oval-field">
                    <label htmlFor="email">Email</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} />
                  </div>
                  <div className="oval-field">
                    <label htmlFor="phoneNumber">Phone Number</label>
                    <PhoneInput
                      country={'cr'}
                      value={formData.phoneNumber}
                      onChange={handlePhoneChange}
                      className="oval-phone-input"
                      inputProps={{ name: 'phoneNumber', id: 'phoneNumber' }}
                    />
                  </div>
                  {/* Row 4: Password - Confirm Password */}
                  <div className="oval-field">
                    <label htmlFor="password">Password</label>
                    <div className="oval-password-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                      />
                      <span className="oval-eye-icon" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? (
                          // Icono de ojo cerrado (con slash)
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M1 1l22 22"></path>
                            <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5.05 0-9.27-3.15-11-7.5a21.15 21.15 0 0 1 5.06-6.07"></path>
                            <path d="M9.88 9.88A3 3 0 0 1 14.12 14.12"></path>
                          </svg>
                        ) : (
                          // Icono de ojo abierto
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="oval-field">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <div className="oval-password-wrapper">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                      />
                      <span className="oval-eye-icon" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        {showConfirmPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M1 1l22 22"></path>
                            <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5.05 0-9.27-3.15-11-7.5a21.15 21.15 0 0 1 5.06-6.07"></path>
                            <path d="M9.88 9.88A3 3 0 0 1 14.12 14.12"></path>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        )}
                      </span>
                    </div>
                  </div>
                  {/* Row 5 (span 2 cols): Checkbox TOS */}
                  <div className="oval-span-2">
                    <div className="oval-terms-container">
                      <input type="checkbox" id="terms" checked={termsAccepted} onChange={handleTermsChange} />
                      <label htmlFor="terms">
                        I’ve read and agree with{" "}
                        <a href="/termsAndConditions" target="_blank" rel="noopener noreferrer">Terms of Service</a> and our{" "}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <button onClick={handleSubmit} disabled={!termsAccepted} className="oval-signup-main-button">
                Create Account
              </button>
              <a className="oval-back-to-home" href="/">Back to Home →</a>
            </div>
          ) : (
            <div className="oval-code-verification">
              <h2>Verify Email</h2>
              <h3>Please check your email and copy the code here:</h3>
              <div className="oval-code-inputs">
                {verificationCode.map((digit, index) => (
                  <input
                    key={index}
                    id={`code-input-${index}`}
                    maxLength={1}
                    className="oval-text-input"
                    value={digit}
                    onChange={(e) => handleVerificationCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                  />
                ))}
              </div>
              <button onClick={handleVerification}>Verify</button>
              <a className="oval-back-to-home" href="/">Back to Home →</a>
            </div>
          )}
        </div>
      </div>

      {/* API Key Modal */}
      {showApiKeyModal && apiKey && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#18181b] border border-purple-500/30 rounded-2xl p-6 w-full max-w-md">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-white mb-2">⚠️ Save Your API Key</h2>
              <p className="text-yellow-400 text-sm font-semibold mb-3">
                This is the only time you'll see this key. Please save it securely.
              </p>
              <div className="bg-black/60 border border-white/20 rounded-lg p-4 mb-4">
                <p className="text-xs text-gray-400 mb-2">Your API Key:</p>
                <p className="text-white font-mono text-sm break-all">{apiKey}</p>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleCopyApiKey}
                className="w-full bg-primary hover:bg-violet-700 text-white px-4 py-3 rounded-lg transition-colors duration-200 font-medium"
              >
                📋 Copy to Clipboard
              </button>
              
              <div className="flex gap-3">
                <button
                  onClick={handleDownloadJSON}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg transition-colors duration-200 font-medium"
                >
                  📄 Download JSON
                </button>
                <button
                  onClick={handleDownloadTXT}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg transition-colors duration-200 font-medium"
                >
                  📝 Download TXT
                </button>
              </div>

              <button
                onClick={() => {
                  setShowApiKeyModal(false);
                  localStorage.removeItem('signup_email');
                  const email = localStorage.getItem('signup_email') || formData.email;
                  navigate('/login', { 
                    state: { 
                      message: 'Email verified successfully! Please log in.',
                      email: email 
                    }
                  });
                }}
                className="w-full bg-gray-700 hover:bg-gray-800 text-white px-4 py-3 rounded-lg transition-colors duration-200 font-medium"
              >
                I've Saved My Key
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-4 text-center">
              You can refresh your API key later from your account settings if needed.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
